import React from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  formatCurrency,
  formatOrderDate,
  computeMargin,
  getShopifyOrderUrl,
  getAmazonOrderUrl,
  STATUS_DOT_COLORS,
} from './ordersHelpers';
import { ROUTES } from '../../consts/general';

const getProductCatalogUrl = (sku) => {
  const params = new URLSearchParams({
    viewTable: 'default',
    search: sku,
    page: '1',
    itemsPerPage: '50',
    statuses: '',
    categories: '',
    subcategories: '',
    brandPillars: '',
    tiers: '',
    entryEngines: '',
    intents: '',
    containerGroups: '',
    products: '',
  });
  return `${ROUTES.PRODUCTS_CATALOG}?${params.toString()}`;
};

const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mt-6 mb-2">
    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueClassName = '' }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-100 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={`text-gray-900 ${valueClassName}`}>
      {value === null || value === undefined || value === '' ? (
        <span className="text-gray-300">&mdash;</span>
      ) : (
        value
      )}
    </span>
  </div>
);

const OrderDetailsDrawer = ({ order, onClose }) => {
  if (!order) return null;

  const items = order.OrderLineItems || [];
  const statusColors = STATUS_DOT_COLORS(order.order_status, order.channel);
  const [dotColor, textColor] = statusColors.split(' ');
  const totalPaid = order.display_total_paid ?? order.total_paid;
  const netRevenue =
    totalPaid != null
      ? parseFloat(totalPaid || 0) - parseFloat(order.refund_total || 0)
      : null;
  const margin = computeMargin(order);
  const externalUrl =
    order.channel === 'Shopify'
      ? getShopifyOrderUrl(order.external_order_id)
      : order.channel === 'Amazon'
        ? getAmazonOrderUrl(order.external_order_id)
        : null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <div className="fixed inset-0 sm:inset-y-0 sm:left-auto sm:right-0 w-full sm:w-[440px] bg-white shadow-xl z-50 flex flex-col">
      <div className="flex items-start justify-between px-5 py-3.5 border-b border-gray-200">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">#{order.external_order_id}</span>
            {order.order_status && (
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${textColor}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                {order.order_status}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {formatOrderDate(order.date_of_order)}
            {order.channel ? ` \u00b7 ${order.channel}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-600"
              title={`Open in ${order.channel}`}
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close order details"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-8">
        <SectionLabel>Order Status</SectionLabel>
        <div className="flex items-center gap-2">
          {order.order_status ? (
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${textColor}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {order.order_status}
            </span>
          ) : (
            <span className="text-gray-300">&mdash;</span>
          )}
          {order.channel && (
            <span className="text-[11px] text-gray-500">
              Set by {order.channel} &middot; synced via API
            </span>
          )}
        </div>

        <SectionLabel>Line Items ({items.length})</SectionLabel>
        <div className="space-y-2">
          {items.length === 0 && (
            <div className="text-sm text-gray-400">No line items</div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 border border-gray-200 rounded-lg p-3"
            >
              {item.Product?.image ? (
                <img
                  src={item.Product.image}
                  alt={item.Product.name}
                  className="h-10 w-10 object-contain rounded flex-shrink-0"
                />
              ) : (
                <div className="h-10 w-10 rounded bg-gray-100 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {item.Product?.name || item.sku || '-'}
                  {item.refunded && (
                    <span className="ml-1 text-red-500 text-xs font-semibold">(refunded)</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {item.sku ? (
                    <Link
                      to={getProductCatalogUrl(item.catalog_sku || item.sku)}
                      className="hover:underline hover:text-blue-600"
                      target="_blank"
                    >
                      {item.sku}
                    </Link>
                  ) : (
                    '-'
                  )}
                  {item.quantity != null && <span> &middot; Qty {item.quantity}</span>}
                </div>
              </div>
              <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                {formatCurrency(item.subtotal) ?? '\u2014'}
              </div>
            </div>
          ))}
        </div>

        <SectionLabel>Financials</SectionLabel>
        <KeyValueRow label="Gross Sales" value={formatCurrency(order.gross_sales)} />
        <KeyValueRow label="Discounts" value={formatCurrency(order.discounts)} />
        <KeyValueRow label="Taxes" value={formatCurrency(order.taxes)} />
        <KeyValueRow label="Shipping" value={formatCurrency(order.shipping_charges)} />
        <KeyValueRow
          label="Refunds"
          value={formatCurrency(order.refund_total)}
          valueClassName={parseFloat(order.refund_total || 0) > 0 ? '!text-red-600' : ''}
        />
        <KeyValueRow
          label="Total Paid"
          value={formatCurrency(totalPaid)}
          valueClassName="font-semibold"
        />
        <KeyValueRow label="Net Revenue" value={formatCurrency(netRevenue)} />
        <KeyValueRow label="Landed Cost" value={formatCurrency(order.landed_cost)} />
        <KeyValueRow label="Weighted Cost" value={formatCurrency(order.weighted_cost)} />
        <KeyValueRow
          label="Est. Margin"
          value={margin != null ? `${margin.toFixed(1)}%` : null}
          valueClassName="font-semibold"
        />

        <SectionLabel>Customer</SectionLabel>
        <KeyValueRow label="Email" value={order.customer_email} />
        <KeyValueRow label="Customer ID" value={order.customer_id} />

        <SectionLabel>Attribution</SectionLabel>
        <KeyValueRow label="UTM Source" value={order.utm_source} />
        <KeyValueRow label="UTM Medium" value={order.utm_medium} />
        <KeyValueRow label="UTM Campaign" value={order.utm_campaign} />
        <KeyValueRow label="Referring Site" value={order.referring_site} />

        {order.box_name && (
          <>
            <SectionLabel>Fulfillment</SectionLabel>
            <KeyValueRow label="Box Name" value={order.box_name} />
            <KeyValueRow label="Earliest Ship" value={formatOrderDate(order.earliest_ship_date) || null} />
            <KeyValueRow label="Latest Ship" value={formatOrderDate(order.latest_ship_date) || null} />
          </>
        )}
      </div>
      </div>
    </>
  );
};

export default OrderDetailsDrawer;
